import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { Checkbox, Col, Divider } from 'antd'
import { Country, KudosLegalEntityVisibilityFilterInput, LegalEntityTiny } from '../../../types'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { CountrySelect } from './CountrySelect'
import { KUDOS_VISIBILITY_PAGE_SIZE } from '../../../graphql/kudos/util'
import { useLegalEntitySearch } from '../../../graphql/legalEntities/hooks/useLegalEntitySearch'
import { LegalEntitySearchSelect } from './LegalEntitySearchSelect'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxButtonGroupElement } from '../../../components/custom_components/InfoBox/InfoBoxButtonGroup'
import { buttonGroupButton } from '../../../components/custom_components/ButtonGroup'
import { useAuth } from '../../../context/auth-context'
import { getCountriesWhereUserIsHr } from '../../user/util'

interface KudosLegalEntityVisibilityInfoBoxProps {
  filters: KudosLegalEntityVisibilityFilterInput
  onFiltersChange: (filters: KudosLegalEntityVisibilityFilterInput) => void
  swichContentButtons: buttonGroupButton[]
}

export const KudosLegalEntityVisibilityInfoBox: FC<KudosLegalEntityVisibilityInfoBoxProps> = ({
  filters,
  onFiltersChange,
  swichContentButtons,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const legalEntitySearch = useLegalEntitySearch()
  const hrCountries = user.roles ? getCountriesWhereUserIsHr(user.roles) : []

  // Handle legal entity selection
  const handleLegalEntityOptionClick = useCallback(
    (le: LegalEntityTiny) => {
      onFiltersChange({
        ...filters,
        legalEntityId: le.id,
        legalEntityName: le.name,
        country: undefined,
      })
      legalEntitySearch.handleClear()
    },
    [filters, onFiltersChange, legalEntitySearch],
  )

  // Handle clearing legal entity selection
  const handleLegalEntityClear = useCallback(() => {
    onFiltersChange({
      ...filters,
      legalEntityId: undefined,
      legalEntityName: undefined,
    })
    legalEntitySearch.handleClear()
  }, [filters, onFiltersChange, legalEntitySearch])

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country | null) => {
      onFiltersChange({
        ...filters,
        country: country || undefined,
        legalEntityId: country ? undefined : filters.legalEntityId,
      })
    },
    [filters, onFiltersChange],
  )

  const handleClearFilters = () => {
    legalEntitySearch.handleClear()
    onFiltersChange({
      status: { kudosDisabled: false, kudosEnabled: false },
      legalEntityId: undefined,
      legalEntityName: undefined,
      country: undefined,
      pagination: {
        offset: 0,
        limit: KUDOS_VISIBILITY_PAGE_SIZE,
      },
    })
  }

  const hasActiveFilters = () => {
    return !!(filters.legalEntityId || filters.country || filters.status.kudosEnabled || filters.status.kudosDisabled)
  }

  return (
    <PerInfoBox>
      <Col span={17} className="fullHeight">
        <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
          {/* Header with title */}
          <PerText appearance="tile-heading">{t('kudos.management.tabs.header.legalEntityVisibility')}</PerText>

          {/* Disclaimer */}
          <PerText fontSize="s">{t('kudos.management.tabs.legalEntityVisibilityDisclaimer')}</PerText>

          {/* Filters */}
          <PerFlexRow>
            <PerFlexRow width="fullWidth">
              {/* Legal Entity Filter */}
              <Col span={7}>
                <PerFlexRow direction="column">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                    {t('kudos.management.filters.legalEntity')}
                  </PerText>
                  <LegalEntitySearchSelect
                    searchTerm={legalEntitySearch.searchTerm}
                    onSearch={legalEntitySearch.handleSearch}
                    onClear={handleLegalEntityClear}
                    legalEntities={legalEntitySearch.legalEntities}
                    loading={legalEntitySearch.loading}
                    shouldShowOptions={legalEntitySearch.shouldShowOptions}
                    onOptionClick={handleLegalEntityOptionClick}
                    placeholder={t('kudos.management.filters.legalEntityPlaceholder')}
                    selectedLegalEntity={
                      filters.legalEntityId && filters.legalEntityName ? { id: filters.legalEntityId, name: filters.legalEntityName } : null
                    }
                  />
                </PerFlexRow>
              </Col>

              {/* Country Filter */}
              <Col span={7}>
                <PerFlexRow direction="column">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                    {t('kudos.management.filters.country')}
                  </PerText>
                  <CountrySelect
                    countries={hrCountries}
                    selectedCountry={filters.country || null}
                    onCountrySelect={handleCountrySelect}
                    placeholder={t('kudos.management.filters.countryPlaceholder')}
                    loading={false}
                  />
                </PerFlexRow>
              </Col>

              {/* Status Filter */}
              <Col span={7}>
                <PerFlexRow direction="column">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                    {t('kudos.management.filters.visibility')}
                  </PerText>
                  <PerFlexRow>
                    <PerFlexRow align="middle" mr="xs">
                      <Checkbox
                        checked={filters.status.kudosEnabled}
                        onChange={(e) => {
                          onFiltersChange({
                            ...filters,
                            status: { ...filters.status, kudosEnabled: e.target.checked },
                          })
                        }}
                      />
                      <PerText fontSize="s" color="secondary" ml="xxs">
                        {t('kudos.table.enabled')}
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow align="middle">
                      <Checkbox
                        checked={filters.status.kudosDisabled}
                        onChange={(e) => {
                          onFiltersChange({
                            ...filters,
                            status: { ...filters.status, kudosDisabled: e.target.checked },
                          })
                        }}
                      />
                      <PerText fontSize="s" color="secondary" ml="xxs">
                        {t('kudos.table.disabled')}
                      </PerText>
                    </PerFlexRow>
                  </PerFlexRow>
                </PerFlexRow>
              </Col>

              {/* Reset Filters Button */}
              <Col span={3}>
                <PerFlexRow justify="end" align="bottom" className="fullHeight">
                  <PerButtonCircle
                    color="error"
                    icon="delete_fill"
                    variant="pastel"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters()}
                    tooltip={t('kudos.management.filters.clear')}
                  />
                </PerFlexRow>
              </Col>
            </PerFlexRow>
          </PerFlexRow>
        </PerFlexRow>
      </Col>

      <>
        <Divider type="vertical" className="fullHeight" />
        <Col span={6} className="fullHeight">
          <InfoBoxTile heading={t('employeeList.infoBox.switchContent')} contentFlex="fixed" headingAlign="center">
            <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
              <PerText fontSize="s" color="secondary" appearance="quote" align="center">
                {t('kudos.settings.switchContentDescription')}
              </PerText>
              <PerInfoBoxButtonGroupElement buttons={swichContentButtons} />
            </PerFlexRow>
          </InfoBoxTile>
        </Col>
      </>
    </PerInfoBox>
  )
}

export default KudosLegalEntityVisibilityInfoBox
